import React from 'react';
import { TrendingDown, Receipt, PlugZap, FileQuestion, Sun, Battery, Building2, BarChart3 } from 'lucide-react';
import { problemsData, solutionData } from '../../data/mock';

const iconMap = {
  TrendingDown: TrendingDown,
  Receipt: Receipt,
  PlugZap: PlugZap,
  FileQuestion: FileQuestion,
  Sun: Sun,
  Battery: Battery,
  Building2: Building2,
  BarChart3: BarChart3
};

// Consistent SVG Arrow Connector Component
const ArrowConnector = ({ direction = 'horizontal', size = 'md' }) => {
  const strokeColor = '#C7CED6';
  const strokeWidth = 2;
  
  if (direction === 'vertical') {
    // Vertical connector for mobile
    return (
      <div className="flex flex-col items-center py-1">
        <svg 
          width="24" 
          height="32" 
          viewBox="0 0 24 32" 
          fill="none" 
          className="overflow-visible"
        >
          {/* Vertical line */}
          <line 
            x1="12" 
            y1="0" 
            x2="12" 
            y2="24" 
            stroke={strokeColor} 
            strokeWidth={strokeWidth} 
            strokeLinecap="round"
          />
          {/* Arrowhead */}
          <path 
            d="M7 20L12 26L17 20" 
            stroke={strokeColor} 
            strokeWidth={strokeWidth} 
            strokeLinecap="round" 
            strokeLinejoin="round"
            fill="none"
          />
        </svg>
      </div>
    );
  }
  
  // Horizontal connector for desktop
  const width = size === 'lg' ? 64 : 48;
  
  return (
    <div className="flex items-center justify-center" style={{ width: `${width}px` }}>
      <svg 
        width={width} 
        height="24" 
        viewBox={`0 0 ${width} 24`} 
        fill="none" 
        className="overflow-visible"
      >
        {/* Horizontal line */}
        <line 
          x1="0" 
          y1="12" 
          x2={width - 10} 
          y2="12" 
          stroke={strokeColor} 
          strokeWidth={strokeWidth} 
          strokeLinecap="round"
        />
        {/* Arrowhead */}
        <path 
          d={`M${width - 14} 7L${width - 4} 12L${width - 14} 17`}
          stroke={strokeColor} 
          strokeWidth={strokeWidth} 
          strokeLinecap="round" 
          strokeLinejoin="round"
          fill="none"
        />
      </svg>
    </div>
  );
};

const ProblemSolutionSection = () => {
  return (
    <section id="solution" className="py-12 sm:py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Problems */}
        <div className="mb-12 sm:mb-16 lg:mb-20">
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs sm:text-sm font-medium mb-3 sm:mb-4 border border-amber-200">
              Die Herausforderung
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-800">
              Aktuelle Hürden im Energiesektor
            </h2>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {problemsData.map((problem, index) => {
              const Icon = iconMap[problem.icon];
              return (
                <div 
                  key={index}
                  className="group p-4 sm:p-6 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 transition-all"
                >
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-amber-100 flex items-center justify-center mb-3 sm:mb-4">
                    <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-amber-700" />
                  </div>
                  <h3 className="text-slate-800 font-medium text-sm sm:text-base leading-tight">{problem.title}</h3>
                </div>
              );
            })}
          </div>
        </div>

        {/* Solution */}
        <div className="relative">
          <div className="text-center mb-8 sm:mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs sm:text-sm font-medium mb-3 sm:mb-4 border border-emerald-200">
              {solutionData.title}
            </span>
            <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-slate-800 mb-3 sm:mb-4">
              Integriertes Quartiers-Energiesystem
            </h2>
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto px-4">
              {solutionData.description}
            </p>
          </div>

          {/* Technical Image - Energy System Visualization */}
          <div className="relative mb-8 sm:mb-12">
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-md sm:shadow-lg">
              <img 
              src={process.env.REACT_APP_SOLUTION_IMAGE || "https://customer-assets.emergentagent.com/job_bess-living/artifacts/2zbn0wa9_QartierEnergy1.jpg"}
                alt="Integriertes Quartiers-Energiesystem mit Photovoltaik und Batteriespeicher"
                className="w-full h-auto object-cover"
              />
            </div>
            {/* Caption */}
            <p className="mt-3 text-xs sm:text-sm text-slate-500 text-center italic px-4">
              Quartiers-Energiesystem mit PV, Batteriespeicher und intelligenter Steuerung
            </p>
          </div>

          {/* Energy Flow Diagram - Consistent Grid Layout */}
          <div className="relative bg-slate-50 rounded-xl sm:rounded-2xl border border-slate-200 p-6 sm:p-8 md:p-12">
            {/* Mobile: Vertical Flow */}
            <div className="flex flex-col items-center md:hidden">
              {solutionData.flowSteps.map((step, index) => {
                const Icon = iconMap[step.icon];
                return (
                  <React.Fragment key={index}>
                    <div className="flex flex-col items-center group">
                      <div className="w-16 h-16 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center group-hover:border-cyan-300 group-hover:shadow-md transition-all">
                        <Icon className="w-8 h-8 text-cyan-700" />
                      </div>
                      <span className="mt-2 text-sm font-medium text-slate-700 text-center">{step.label}</span>
                    </div>
                    {index < solutionData.flowSteps.length - 1 && (
                      <ArrowConnector direction="vertical" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>

            {/* Desktop: Horizontal Flow with Grid */}
            <div className="hidden md:grid md:grid-cols-7 md:items-center md:justify-items-center md:gap-0">
              {solutionData.flowSteps.map((step, index) => {
                const Icon = iconMap[step.icon];
                const isLast = index === solutionData.flowSteps.length - 1;
                return (
                  <React.Fragment key={index}>
                    {/* Card */}
                    <div className="flex flex-col items-center group">
                      <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center group-hover:border-cyan-300 group-hover:shadow-md transition-all">
                        <Icon className="w-10 h-10 lg:w-12 lg:h-12 text-cyan-700" />
                      </div>
                      <span className="mt-3 text-sm lg:text-base font-medium text-slate-700 text-center whitespace-nowrap">{step.label}</span>
                    </div>
                    {/* Connector */}
                    {!isLast && (
                      <ArrowConnector direction="horizontal" size="lg" />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSolutionSection;
